import React, { useCallback, useEffect, useRef, useState } from 'react';
import { vuser } from '../services/api';
import type { RagDocument, RagDocumentStatus, RagFolder } from '../types/rag';
import { getErrorMessage, isLikelyTransientFetchError } from '../utils/errorUtils';
import ConfirmModal from './ConfirmModal';
import './ConnectorRagDocumentsPanel.css';

const ALLOWED_MIME = new Set([
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const FOLDER_NAME_MAX = 120;

/** Shown next to Upload and in tooltips (must match ALLOWED_MIME / accept). */
const RAG_UPLOAD_FORMATS_HINT = 'Supported formats: PDF, TXT, DOC, DOCX.';
const RAG_UPLOAD_FORMATS_TITLE =
  'Choose a file to upload. Accepted types: PDF, TXT, Microsoft Word (.doc, .docx).';

const EXT_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

function resolveMime(file: File): string | null {
  if (file.type && ALLOWED_MIME.has(file.type)) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext && EXT_TO_MIME[ext]) return EXT_TO_MIME[ext];
  return null;
}

function pickData<T>(resp: { success?: boolean; data?: T; error?: { message?: string } }): T {
  if (resp?.success === false) {
    throw new Error(resp.error?.message || 'Request failed');
  }
  if (resp?.data === undefined) {
    throw new Error('Empty response');
  }
  return resp.data;
}

function statusClass(s: RagDocumentStatus): string {
  if (s === 'READY') return 'rag-doc-status rag-doc-status--ready';
  if (s === 'ERROR') return 'rag-doc-status rag-doc-status--error';
  return 'rag-doc-status rag-doc-status--processing';
}

/** Folder assignment and usable toggle are only allowed after ingest completes. */
function isRagDocumentProcessing(doc: Pick<RagDocument, 'status'>): boolean {
  return String(doc.status || '').toUpperCase() === 'PROCESSING';
}

type DocListFilter =
  | { kind: 'all' }
  | { kind: 'unassigned' }
  | { kind: 'folder'; folderId: string };

type FolderEditor =
  | null
  | { mode: 'create' }
  | { mode: 'edit'; folder: RagFolder };

export interface ConnectorRagDocumentsPanelProps {
  integrationId: string;
}

const ConnectorRagDocumentsPanel: React.FC<ConnectorRagDocumentsPanelProps> = ({ integrationId }) => {
  const [folders, setFolders] = useState<RagFolder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [documents, setDocuments] = useState<RagDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docListFilter, setDocListFilter] = useState<DocListFilter>({ kind: 'all' });
  const [uploadFolderId, setUploadFolderId] = useState('');
  const [folderEditor, setFolderEditor] = useState<FolderEditor>(null);
  const [folderFormName, setFolderFormName] = useState('');
  const [folderFormDescription, setFolderFormDescription] = useState('');
  const [folderNameError, setFolderNameError] = useState<string | null>(null);
  const [folderSaving, setFolderSaving] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<RagFolder | null>(null);
  const [deleteFolderLoading, setDeleteFolderLoading] = useState(false);
  const [deleteDocumentTarget, setDeleteDocumentTarget] = useState<RagDocument | null>(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [assigningDocId, setAssigningDocId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const folderNameById = useCallback(
    (id: string | null | undefined) => {
      if (!id) return null;
      return folders.find((f) => f.id === id)?.name ?? null;
    },
    [folders]
  );

  const loadFolders = useCallback(async () => {
    setFoldersLoading(true);
    try {
      const resp = await vuser.ragListFolders(integrationId);
      const data = pickData(resp);
      setFolders(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load folders'));
      setFolders([]);
    } finally {
      setFoldersLoading(false);
    }
  }, [integrationId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let resp;
      if (docListFilter.kind === 'folder') {
        resp = await vuser.ragListDocuments(integrationId, { folder_id: docListFilter.folderId });
      } else {
        resp = await vuser.ragListDocuments(integrationId);
      }
      let data = pickData(resp);
      if (!Array.isArray(data)) data = [];
      if (docListFilter.kind === 'unassigned') {
        data = data.filter((d) => !d.folder_id);
      }
      setDocuments(data);
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to load documents'));
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [integrationId, docListFilter]);

  useEffect(() => {
    void loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreateFolder = () => {
    setFolderFormName('');
    setFolderFormDescription('');
    setFolderNameError(null);
    setFolderEditor({ mode: 'create' });
  };

  const openEditFolder = (folder: RagFolder) => {
    setFolderFormName(folder.name);
    setFolderFormDescription(folder.description || '');
    setFolderNameError(null);
    setFolderEditor({ mode: 'edit', folder });
  };

  const saveFolder = async () => {
    const name = folderFormName.trim();
    if (!name) {
      setFolderNameError('Folder name is required.');
      return;
    }
    if (name.length > FOLDER_NAME_MAX) {
      setFolderNameError(`Folder name must be at most ${FOLDER_NAME_MAX} characters.`);
      return;
    }
    setFolderSaving(true);
    setFolderNameError(null);
    setError(null);
    try {
      if (folderEditor?.mode === 'create') {
        pickData(
          await vuser.ragCreateFolder(integrationId, {
            name,
            description: folderFormDescription.trim() ? folderFormDescription.trim() : undefined,
          })
        );
      } else if (folderEditor?.mode === 'edit') {
        pickData(
          await vuser.ragUpdateFolder(integrationId, folderEditor.folder.id, {
            name,
            description: folderFormDescription.trim(),
          })
        );
      }
      setFolderEditor(null);
      await loadFolders();
      await load();
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to save folder'));
    } finally {
      setFolderSaving(false);
    }
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolderTarget) return;
    setDeleteFolderLoading(true);
    setError(null);
    try {
      pickData(await vuser.ragDeleteFolder(integrationId, deleteFolderTarget.id));
      if (docListFilter.kind === 'folder' && docListFilter.folderId === deleteFolderTarget.id) {
        setDocListFilter({ kind: 'all' });
      }
      if (uploadFolderId === deleteFolderTarget.id) setUploadFolderId('');
      setDeleteFolderTarget(null);
      await loadFolders();
      await load();
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to delete folder'));
    } finally {
      setDeleteFolderLoading(false);
    }
  };

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const mime = resolveMime(file);
    if (!mime) {
      setError('Unsupported file type. Use PDF, TXT, DOC, or DOCX.');
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const folderForUpload = uploadFolderId.trim();
      const presign = pickData(
        await vuser.ragPresignDocument(integrationId, {
          filename: file.name,
          mime_type: mime,
          folder_id: folderForUpload ? folderForUpload : null,
        })
      );
      const docId = presign.document?.doc_id;
      if (!docId) throw new Error('Presign response missing doc_id');

      try {
        await vuser.ragUploadToPresignedUrl(presign.upload_url, file, mime);
      } catch (putErr) {
        if (!isLikelyTransientFetchError(putErr)) throw putErr;
        try {
          pickData(await vuser.ragNotifyDocumentUploaded(integrationId, docId));
          await load();
          return;
        } catch {
          throw putErr;
        }
      }
      pickData(await vuser.ragNotifyDocumentUploaded(integrationId, docId));
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const toggleUsable = async (doc: RagDocument) => {
    if (isRagDocumentProcessing(doc)) return;
    setError(null);
    try {
      const next = pickData(await vuser.ragSetDocumentUsableByPath(integrationId, doc.doc_id, !doc.usable));
      setDocuments((prev) => prev.map((d) => (d.doc_id === next.doc_id ? next : d)));
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to update document'));
    }
  };

  const confirmDeleteDocument = async () => {
    if (!deleteDocumentTarget) return;
    const documentId = deleteDocumentTarget.id;
    const docId = deleteDocumentTarget.doc_id;
    setDeleteDocumentLoading(true);
    setError(null);
    try {
      pickData(await vuser.ragDeleteDocument(integrationId, documentId));
      setDeleteDocumentTarget(null);
      setDocuments((prev) => prev.filter((d) => d.doc_id !== docId));
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to delete document'));
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  const assignDocumentFolder = async (doc: RagDocument, folderId: string | null) => {
    if (isRagDocumentProcessing(doc)) return;
    setAssigningDocId(doc.doc_id);
    setError(null);
    try {
      const next = pickData(await vuser.ragPatchDocumentFolder(integrationId, doc.doc_id, folderId));
      setDocuments((prev) => prev.map((d) => (d.doc_id === next.doc_id ? next : d)));
    } catch (e) {
      setError(getErrorMessage(e, 'Failed to update document folder'));
    } finally {
      setAssigningDocId(null);
    }
  };

  const docFilterSelectValue =
    docListFilter.kind === 'all'
      ? 'all'
      : docListFilter.kind === 'unassigned'
        ? 'unassigned'
        : `folder:${docListFilter.folderId}`;

  return (
    <div className="rag-doc-root" id="rag-documents-content">
      <section className="rag-doc-section" aria-labelledby="rag-folders-heading">
        <div className="rag-doc-section-head">
          <h3 id="rag-folders-heading">Folders</h3>
          <div className="rag-doc-section-actions">
            <button
              type="button"
              className="rag-doc-btn"
              onClick={() => void loadFolders()}
              disabled={foldersLoading}
            >
              <i className="fas fa-sync-alt" aria-hidden /> Refresh
            </button>
            <button type="button" className="rag-doc-btn rag-doc-btn--primary" onClick={openCreateFolder}>
              <i className="fas fa-folder-plus" aria-hidden /> New folder
            </button>
          </div>
        </div>
        <div className="rag-doc-table-wrap">
          <table className="rag-doc-table rag-doc-table--folders">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {foldersLoading && folders.length === 0 ? (
                <tr>
                  <td colSpan={4} className="rag-doc-empty">
                    Loading folders…
                  </td>
                </tr>
              ) : folders.length === 0 ? (
                <tr>
                  <td colSpan={4} className="rag-doc-empty">
                    No folders yet. Create one to organize documents (optional).
                  </td>
                </tr>
              ) : (
                folders.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <div className="rag-doc-name">{f.name}</div>
                      <div className="rag-doc-meta mono">{f.id}</div>
                    </td>
                    <td className="rag-doc-folder-desc">{f.description || '—'}</td>
                    <td className="rag-doc-meta">{new Date(f.updated_at).toLocaleString()}</td>
                    <td className="rag-doc-actions">
                      <button type="button" className="rag-doc-link" onClick={() => openEditFolder(f)}>
                        Edit
                      </button>
                      <button type="button" className="rag-doc-link rag-doc-link--danger" onClick={() => setDeleteFolderTarget(f)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="rag-doc-toolbar" id="rag-documents-toolbar">
        <div className="rag-doc-upload-block">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.doc,.docx,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="rag-doc-file-input"
            onChange={(ev) => void onPickFile(ev)}
            disabled={uploading}
          />
          <div className="rag-doc-upload-actions">
            <button
              type="button"
              className="rag-doc-btn rag-doc-btn--primary"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              title={RAG_UPLOAD_FORMATS_TITLE}
              aria-describedby="rag-upload-formats-hint"
            >
              {uploading ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden /> Uploading…
                </>
              ) : (
                <>
                  <i className="fas fa-cloud-upload-alt" aria-hidden /> Upload document
                </>
              )}
            </button>
            <button type="button" className="rag-doc-btn" onClick={() => void load()} disabled={loading}>
              <i className="fas fa-sync-alt" aria-hidden /> Refresh
            </button>
          </div>
          <p id="rag-upload-formats-hint" className="rag-doc-upload-hint" role="note">
            {RAG_UPLOAD_FORMATS_HINT}
          </p>
        </div>

        <div className="rag-doc-folder-filter">
          <label>
            Upload into folder
            <select
              className="rag-doc-select"
              value={uploadFolderId}
              onChange={(e) => setUploadFolderId(e.target.value)}
              disabled={uploading}
            >
              <option value="">No folder</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="rag-doc-folder-filter">
          <label>
            Show documents
            <select
              className="rag-doc-select"
              value={docFilterSelectValue}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'all') setDocListFilter({ kind: 'all' });
                else if (v === 'unassigned') setDocListFilter({ kind: 'unassigned' });
                else if (v.startsWith('folder:')) setDocListFilter({ kind: 'folder', folderId: v.slice(7) });
              }}
            >
              <option value="all">All documents</option>
              <option value="unassigned">No folder</option>
              {folders.map((f) => (
                <option key={f.id} value={`folder:${f.id}`}>
                  Folder: {f.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && (
        <div className="connector-message error rag-doc-error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden /> {error}
        </div>
      )}

      <div className="rag-doc-table-wrap">
        <table className="rag-doc-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Folder</th>
              <th>Status</th>
              <th>Usable</th>
              <th>Tokens / cost</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && documents.length === 0 ? (
              <tr>
                <td colSpan={7} className="rag-doc-empty">
                  Loading…
                </td>
              </tr>
            ) : documents.length === 0 ? (
              <tr>
                <td colSpan={7} className="rag-doc-empty">
                  No documents yet. Upload a PDF, TXT, DOC, or DOCX file.
                </td>
              </tr>
            ) : (
              documents.map((doc) => {
                const processing = isRagDocumentProcessing(doc);
                return (
                <tr key={doc.id}>
                  <td>
                    <div className="rag-doc-name">{doc.filename}</div>
                    <div className="rag-doc-meta">{doc.mime_type}</div>
                    <div className="rag-doc-meta mono">{doc.doc_id}</div>
                  </td>
                  <td>
                    <select
                      className="rag-doc-select rag-doc-select--compact"
                      value={doc.folder_id ?? ''}
                      disabled={processing || assigningDocId === doc.doc_id}
                      onChange={(e) => {
                        const v = e.target.value;
                        void assignDocumentFolder(doc, v === '' ? null : v);
                      }}
                      title={
                        processing
                          ? 'Folder can be changed after the document finishes processing'
                          : 'Move to folder (does not re-ingest)'
                      }
                    >
                      <option value="">No folder</option>
                      {folders.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                    {doc.folder_id && !folderNameById(doc.folder_id) && (
                      <div className="rag-doc-meta mono" title={doc.folder_id}>
                        Unknown folder id
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={statusClass(doc.status)}>{doc.status}</span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`rag-doc-toggle ${doc.usable ? 'rag-doc-toggle--on' : ''}`}
                      disabled={processing}
                      onClick={() => void toggleUsable(doc)}
                      title={
                        processing
                          ? 'Usable can be changed after the document finishes processing'
                          : doc.usable
                            ? 'Disable for RAG'
                            : 'Enable for RAG'
                      }
                    >
                      {doc.usable ? 'On' : 'Off'}
                    </button>
                  </td>
                  <td className="rag-doc-tokens">
                    {doc.ingest_usage ? (
                      <>
                        <span title="Total tokens">{doc.ingest_usage.total_tokens.toLocaleString()} tok</span>
                        {typeof doc.ingest_usage.total_cost === 'number' && (
                          <span className="rag-doc-meta"> · ${doc.ingest_usage.total_cost.toFixed(6)}</span>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="rag-doc-meta">{new Date(doc.updated_at).toLocaleString()}</td>
                  <td className="rag-doc-actions">
                    <button
                      type="button"
                      className="rag-doc-btn rag-doc-btn--compact rag-doc-btn--danger"
                      onClick={() => setDeleteDocumentTarget(doc)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      {folderEditor && (
        <div
          className="rag-doc-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rag-folder-modal-title"
        >
          <div className="rag-doc-modal">
            <div className="rag-doc-modal__head">
              <h4 id="rag-folder-modal-title">{folderEditor.mode === 'create' ? 'Create folder' : 'Edit folder'}</h4>
              <button
                type="button"
                className="rag-doc-modal__close"
                onClick={() => {
                  if (folderSaving) return;
                  setFolderNameError(null);
                  setFolderEditor(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="rag-doc-modal__body">
              <label
                className={`rag-doc-modal__field${folderNameError ? ' rag-doc-modal__field--invalid' : ''}`}
              >
                Name <span className="rag-doc-modal__hint">(required, max {FOLDER_NAME_MAX})</span>
                <input
                  type="text"
                  value={folderFormName}
                  maxLength={FOLDER_NAME_MAX}
                  aria-invalid={Boolean(folderNameError)}
                  aria-describedby={folderNameError ? 'rag-folder-name-error' : undefined}
                  onChange={(e) => {
                    setFolderFormName(e.target.value.slice(0, FOLDER_NAME_MAX));
                    if (folderNameError) setFolderNameError(null);
                  }}
                  placeholder="e.g. Policies"
                />
                {folderNameError ? (
                  <span id="rag-folder-name-error" className="rag-doc-modal__error" role="alert">
                    {folderNameError}
                  </span>
                ) : null}
              </label>
              <label className="rag-doc-modal__field">
                Description
                <textarea
                  rows={3}
                  value={folderFormDescription}
                  onChange={(e) => setFolderFormDescription(e.target.value)}
                  placeholder="Optional"
                />
              </label>
            </div>
            <div className="rag-doc-modal__foot">
              <button
                type="button"
                className="rag-doc-btn"
                disabled={folderSaving}
                onClick={() => {
                  setFolderNameError(null);
                  setFolderEditor(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rag-doc-btn rag-doc-btn--primary"
                disabled={folderSaving}
                onClick={() => void saveFolder()}
              >
                {folderSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteFolderTarget !== null}
        title="Delete folder"
        message={
          deleteFolderTarget
            ? `Delete folder “${deleteFolderTarget.name}”? Documents in this folder will stay; their folder assignment will be cleared. Ingest is not re-run.`
            : ''
        }
        confirmText="Delete"
        isConfirmLoading={deleteFolderLoading}
        onConfirm={() => void confirmDeleteFolder()}
        onCancel={() => !deleteFolderLoading && setDeleteFolderTarget(null)}
      />

      <ConfirmModal
        isOpen={deleteDocumentTarget !== null}
        title="Delete document"
        variant="danger"
        message={
          deleteDocumentTarget ? (
            <>
              <p className="confirm-modal-line">
                Are you sure you want to delete{' '}
                <strong className="confirm-modal-filename">“{deleteDocumentTarget.filename}”</strong>?
              </p>
              <p className="confirm-modal-line confirm-modal-line--muted">This action cannot be undone.</p>
            </>
          ) : (
            ''
          )
        }
        confirmText="Delete"
        isConfirmLoading={deleteDocumentLoading}
        onConfirm={() => void confirmDeleteDocument()}
        onCancel={() => !deleteDocumentLoading && setDeleteDocumentTarget(null)}
      />
    </div>
  );
};

export default ConnectorRagDocumentsPanel;
